import React, { useEffect, useState } from 'react';
import { copyToClipboard, ShareableContent, studioShareLinks } from '../../utils/sharingUrls';
import './ShareModal.css';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  content: ShareableContent;
  onShare?: (platform: string) => void;
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, content, onShare }) => {
  const [notice, setNotice] = useState('');
  const [copyLabel, setCopyLabel] = useState('Copy link');
  const links = studioShareLinks(content);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => {
      setNotice('');
      setCopyLabel('Copy link');
    }, 2000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  if (!isOpen) return null;

  const track = (platform: string) => {
    onShare?.(platform);
  };

  const open = (url: string, platform: string) => {
    track(platform);
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose();
  };

  const openInstagram = (platform: string, message: string) => {
    copyToClipboard(links.copy).then((ok) => {
      if (ok) {
        setCopyLabel('Copy link');
        setNotice(message);
      }
      track(platform);
      window.open(links.instagram, '_blank', 'noopener,noreferrer');
    });
  };

  const copyLink = () => {
    copyToClipboard(links.copy).then((ok) => {
      if (!ok) return;
      setCopyLabel('Link copied');
      setNotice('Link copied');
      track('copy');
    });
  };

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div
        className="share-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="share-modal-header">
          <h2 id="share-modal-title">Share</h2>
          <button type="button" className="share-modal-close" onClick={onClose} aria-label="Close">
            <i className="fas fa-times" aria-hidden="true" />
          </button>
        </div>
        <div className="share-modal-body">
          {content.title ? <p className="share-modal-subject">{content.title}</p> : null}
          <div className="share-modal-actions">
            <button type="button" className="share-modal-action" onClick={() => open(links.whatsapp, 'whatsapp')}>
              <i className="fab fa-whatsapp" aria-hidden="true" />
              <span>WhatsApp</span>
            </button>
            <button type="button" className="share-modal-action" onClick={() => openInstagram('instagram_post', 'Link copied — paste it into your post')}>
              <i className="fab fa-instagram" aria-hidden="true" />
              <span>Insta post</span>
            </button>
            <button type="button" className="share-modal-action" onClick={() => openInstagram('instagram_story', 'Link copied — paste it into your story')}>
              <i className="fab fa-instagram" aria-hidden="true" />
              <span>Insta story</span>
            </button>
            <button type="button" className="share-modal-action" onClick={() => open(links.facebook, 'facebook')}>
              <i className="fab fa-facebook-f" aria-hidden="true" />
              <span>Facebook</span>
            </button>
            <button type="button" className="share-modal-action" onClick={() => open(links.email, 'email')}>
              <i className="fas fa-envelope" aria-hidden="true" />
              <span>Email</span>
            </button>
            <button type="button" className="share-modal-action" onClick={() => open(links.linkedin, 'linkedin')}>
              <i className="fab fa-linkedin-in" aria-hidden="true" />
              <span>LinkedIn</span>
            </button>
            <button type="button" className="share-modal-action" onClick={copyLink}>
              <i className="fas fa-link" aria-hidden="true" />
              <span>{copyLabel}</span>
            </button>
          </div>
          <p className="share-modal-status" role="status" aria-live="polite">{notice}</p>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
